import { ConsentManager, ConsentManagerVendor, ConsentRequirement, ConsentState } from '../consent.types';
import { deepEqual } from '../utils/script';

export type UsercentricsConfig = {
    settingsId: string;
    // optionales Mapping für sprechende Namen → UC-intern
    vendorMap?: Record<string, string>;
    purposeMap?: Record<string, string>;
    openAs?: 'welcome' | 'preferences';
};

export class UsercentricsAdapter implements ConsentManager {
    public name: ConsentManagerVendor = 'usercentrics';
    private ready = false;
    private lastState: ConsentState = { services: {}, categories: {} };

    // optionale Config-Properties (nur für Mapping/Oeffnungsverhalten)
    private configVendorMap?: Record<string, string>;
    private configPurposeMap?: Record<string, string>;
    private openAs: 'welcome' | 'preferences' = 'welcome';

    async load(config: UsercentricsConfig): Promise<void> {
        if (!config?.settingsId) throw new Error('Usercentrics settingsId missing');

        this.configVendorMap = config.vendorMap ?? {};
        this.configPurposeMap = config.purposeMap ?? {};
        this.openAs = config.openAs ?? 'welcome';

        // 1) Fremde/kaputte loader.js entfernen (index.module.js NICHT anfassen)
        const ucScripts = document.querySelectorAll<HTMLScriptElement>('script[src*="usercentrics.eu/browser-ui/"]');
        ucScripts.forEach((s) => {
            const src = s.getAttribute('src') || '';
            const isLoader =
                /\/browser-ui\/latest\/loader\.js$/.test(src) || /\/browser-ui\/[^/]+\/loader\.js$/.test(src);
            const isOurs = s.id === 'usercentrics-cmp';
            const hasSettings = !!s.getAttribute('data-settings-id');

            if (isLoader && !isOurs && !hasSettings) {
                s.parentElement?.removeChild(s);
            }
        });

        // 2) Unser korrektes loader.js Script sicherstellen
        let scriptEl = document.querySelector<HTMLScriptElement>('#usercentrics-cmp');
        let inserted = false;

        if (!scriptEl) {
            scriptEl = document.createElement('script');
            scriptEl.id = 'usercentrics-cmp';
            scriptEl.src = 'https://app.usercentrics.eu/browser-ui/latest/loader.js';
            scriptEl.async = true;
            scriptEl.setAttribute('data-settings-id', config.settingsId);
            document.head.prepend(scriptEl);
            inserted = true;
        } else {
            if (scriptEl.getAttribute('data-settings-id') !== config.settingsId) {
                scriptEl.setAttribute('data-settings-id', config.settingsId);
            }
            if (!scriptEl.src.includes('/browser-ui/latest/loader.js')) {
                scriptEl.src = 'https://app.usercentrics.eu/browser-ui/latest/loader.js';
            }
        }

        // 3) Auf UC_UI warten (ohne Non-Null!)
        await new Promise<void>((resolve, reject) => {
            const deadline = Date.now() + 15000;

            const finishIfReady = () => {
                if (window.UC_UI && typeof window.UC_UI.getServicesBaseInfo === 'function') {
                    this.ready = true;
                    this.lastState = this.computeState();
                    resolve();
                    return true;
                }
                return false;
            };

            const poll = () => {
                if (finishIfReady()) return;
                if (Date.now() > deadline) {
                    // reject(new Error('Usercentrics did not become ready in time'));
                    console.log('Usercentrics did not become ready in time');
                    reject();

                    return;
                }
                setTimeout(poll, 50);
            };

            // Wenn neu eingefügt: auf load/error hören, zusätzlich sofort prüfen
            if (inserted && scriptEl) {
                const onLoad = () => {
                    finishIfReady() || poll();
                };
                const onError = () => reject(new Error('Usercentrics loader failed to load'));

                scriptEl.addEventListener('load', onLoad, { once: true });
                scriptEl.addEventListener('error', onError, { once: true });

                // Falls aus Cache direkt verfügbar:
                setTimeout(() => {
                    finishIfReady() || poll();
                }, 0);
            } else {
                // Script war schon da → sofort prüfen/pollen
                if (!finishIfReady()) poll();
            }
        });
    }

    isReady(): boolean {
        return this.ready;
    }
    getState(): ConsentState {
        return this.lastState;
    }

    onChange(cb: (state: ConsentState) => void): () => void {
        let stopped = false;
        const tick = () => {
            if (stopped) return;
            const current = this.computeState();
            if (!deepEqual(current, this.lastState)) {
                this.lastState = current;
                cb(current);
            }
            setTimeout(tick, 800);
        };
        tick();

        // Wenn UC ein Event dispatcht, reagieren (best effort)
        const handler = () => {
            const current = this.computeState();
            if (!deepEqual(current, this.lastState)) {
                this.lastState = current;
                cb(current);
            }
        };
        window.addEventListener('ucConsentChanged' as any, handler as any);

        return () => {
            stopped = true;
            window.removeEventListener('ucConsentChanged' as any, handler as any);
        };
    }

    hasConsent(req: ConsentRequirement): boolean {
        const state = this.lastState;

        const resolveService = (k: string) => {
            const mapped = this.configVendorMap?.[k] ?? k;
            return state.services[mapped] ?? state.services[k];
        };
        const okServices = (req.services ?? []).every((s) => resolveService(s) === true);

        const resolvePurpose = (k: string) => {
            const mapped = this.configPurposeMap?.[k] ?? k;
            return state.categories[mapped] ?? state.categories[k];
        };
        const okCategories = (req.categories ?? []).every((c) => resolvePurpose(c) === true);

        return okServices && okCategories;
    }

    showBanner(): void {
        if (this.openAs === 'preferences' && window.UC_UI?.showSecondLayer) {
            window.UC_UI.showSecondLayer();
        } else {
            window.UC_UI?.showFirstLayer?.();
        }
    }

    private computeState(): ConsentState {
        const services = window.UC_UI?.getServicesBaseInfo?.() ?? [];
        const serviceMap: Record<string, boolean> = {};
        const categoryMap: Record<string, boolean> = {};

        for (const s of services) {
            const allowed = !!s?.consent?.status;
            if (s.name) serviceMap[s.name] = allowed;
            if (s.id) serviceMap[s.id] = allowed;

            const cats = s.categories?.length ? s.categories : s.category ? [s.category] : [];
            for (const c of cats) {
                if (!categoryMap[c]) categoryMap[c] = false;
                if (allowed) categoryMap[c] = true;
            }
        }
        return { services: serviceMap, categories: categoryMap };
    }
}
